/**
 * YouTubePublishProgress — real-time view of an in-flight publish job.
 *
 * Combines:
 *   - SSE stream (subscribeToJobEvents) for push updates
 *   - 5 s polling (getPublishJob) as a safety net / initial load
 *
 * Calls onDone() when the job reaches a terminal state (completed / failed).
 *
 * Error display: every message comes from an i18n key chosen by a machine-
 * readable code -- the job's `error_code` (constants/youtube_publish/
 * job_errors.py) or, for a failed status/cancel request, errorSanitizer. The
 * job's `error` prose and the per-language `error` strings are backend
 * diagnostics (exception text, field names, Google API payloads) and are never
 * rendered; they go to the console and, for defects, to Sentry.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import {
  getPublishJob,
  cancelPublishJob,
  subscribeToJobEvents,
} from '../../services/youtubePublishApi';
import type { YouTubePublishJobStatus } from '../../services/youtubePublishApi';
import type {
  YouTubePublishErrorCode,
  YouTubePublishProgress,
} from '../../types/generated/youtubePublish';
import { sanitizeBackendError } from '../../utils/errorSanitizer';
import type { SanitizedError } from '../../utils/errorSanitizer';

interface YouTubePublishProgressProps {
  jobId: string;
  onDone?: () => void;
}

// 'partial' is terminal too: the video published but some languages failed.
const TERMINAL_STATES = new Set<YouTubePublishJobStatus['state']>([
  'completed',
  'failed',
  'partial',
]);

// Message per failure code. UNEXPECTED is a defect, not something the user can
// act on, so it gets fixed publish-specific text. No key here may claim a
// refund: LLM translation calls that already settled stay charged when a
// publish job fails.
const JOB_ERROR_KEYS: Record<YouTubePublishErrorCode, string> = {
  QUOTA_EXCEEDED_PRE_CHECK: 'youtube.publish.jobError.quotaExceeded',
  QUOTA_EXCEEDED_RUNTIME: 'youtube.publish.jobError.quotaExceeded',
  OAUTH_REQUIRED: 'youtube.publish.jobError.oauthRequired',
  VALIDATION_FAILED: 'youtube.publish.jobError.validationFailed',
  CANCELLED: 'youtube.publish.jobError.cancelled',
  SYSTEMIC_FAILURE: 'youtube.publish.jobError.systemicFailure',
  UNEXPECTED: 'youtube.publish.jobError.unexpected',
};

/** i18n key describing a terminal non-success job; null while running or on success. */
function jobFailureKey(job: YouTubePublishJobStatus): string | null {
  if (job.state === 'partial') return 'youtube.publish.jobError.partial';
  if (job.state !== 'failed') return null;
  return JOB_ERROR_KEYS[job.error_code ?? 'UNEXPECTED'];
}

function stateBadgeClass(state: string): string {
  switch (state) {
    case 'completed':
      return 'bg-green-900/40 border-green-500/40 text-green-300';
    case 'failed':
      return 'bg-red-900/40 border-red-500/40 text-red-300';
    case 'partial':
      return 'bg-amber-900/40 border-amber-500/40 text-amber-300';
    case 'pending':
      return 'bg-white/10 border-white/20 text-white/60';
    default:
      return 'bg-purple-900/40 border-purple-500/40 text-purple-300';
  }
}

export function YouTubePublishProgress({ jobId, onDone }: YouTubePublishProgressProps) {
  const { t } = useTranslation();
  const [job, setJob] = useState<YouTubePublishJobStatus | null>(null);
  const [requestError, setRequestError] = useState<SanitizedError | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const doneCalledRef = useRef(false);
  const requestErrorRef = useRef(false);

  // A failed status poll or cancel request. Reported once: the panel keeps
  // showing the first failure, and the 5 s poll would otherwise re-send the
  // same outage to Sentry on every tick.
  const reportRequestError = useCallback((err: unknown) => {
    if (requestErrorRef.current) return;
    requestErrorRef.current = true;
    setRequestError(sanitizeBackendError(err));
  }, []);

  const applyJob = useCallback(
    (data: YouTubePublishJobStatus) => {
      setJob(data);
      if (!TERMINAL_STATES.has(data.state) || doneCalledRef.current) return;
      doneCalledRef.current = true;

      if (data.state === 'completed') {
        // Success: briefly show the completed badge, then return to the form.
        setTimeout(() => onDone?.(), 3_000);
        return;
      }

      // Failed OR partial: do NOT auto-dismiss. The publish POST returned 200, so
      // these (e.g. a quota pre-check rejection, or some languages failing) never
      // reach httpUtils' logger -- nothing showed in the console and the old 3 s
      // auto-return hid the reason. Log the raw diagnostic for a console trace and
      // keep this view up with an explicit Back control (rendered below) so the
      // user can read the (code-derived) reason.
      const reason: string =
        data.error ?? `YouTube publish job ended in state '${data.state}' (no reason reported).`;
      console.error(
        `[YouTubePublish] job ${data.job_id} ${data.state} (${data.error_code ?? 'no code'}): ${reason}`
      );
      // A defect rather than a user-actionable condition: ship the raw record to
      // Sentry through the sanitizer (the user sees JOB_ERROR_KEYS.UNEXPECTED).
      if (data.state === 'failed' && (data.error_code ?? 'UNEXPECTED') === 'UNEXPECTED') {
        sanitizeBackendError(data);
      }
    },
    [onDone]
  );

  // -------------------------------------------------------------------------
  // Initial load + polling (5 s)
  // -------------------------------------------------------------------------
  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        const data = await getPublishJob(jobId);
        applyJob(data);
      } catch (err: unknown) {
        reportRequestError(err);
      }
    };

    void load();
    pollRef.current = setInterval(() => void load(), 5_000);
    return () => {
      if (pollRef.current !== null) clearInterval(pollRef.current);
    };
  }, [jobId, applyJob, reportRequestError]);

  // -------------------------------------------------------------------------
  // SSE subscription
  // -------------------------------------------------------------------------
  useEffect(() => {
    const close = subscribeToJobEvents(jobId, (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data as string) as Partial<YouTubePublishJobStatus>;
        // The publish SSE also carries raw lifecycle frames ({type: 'started'|'failed'|...})
        // that are NOT status records — they lack `state`/`progress`. Applying one would
        // clobber `job` with an object missing those fields and crash the render. Only
        // apply real status frames; the 5s poll is the source of truth for the full shape.
        if (typeof data.state === 'string') {
          applyJob(data as YouTubePublishJobStatus);
        }
      } catch {
        // Ignore unparseable SSE frames
      }
    });
    return close;
  }, [jobId, applyJob]);

  // -------------------------------------------------------------------------
  // Cancel
  // -------------------------------------------------------------------------
  const handleCancel = useCallback(async () => {
    setCancelling(true);
    try {
      await cancelPublishJob(jobId);
    } catch (err: unknown) {
      reportRequestError(err);
    } finally {
      setCancelling(false);
    }
  }, [jobId, reportRequestError]);

  if (requestError) {
    return (
      <div className="bg-black/30 border border-white/10 rounded-lg px-4 py-4 space-y-1">
        <p className="text-red-400 text-sm">{t(requestError.i18nKey)}</p>
        {requestError.errorId && (
          <p className="text-white/40 text-xs">
            {t('error.generic.support_reference', { id: requestError.errorId })}
          </p>
        )}
      </div>
    );
  }

  if (!job) {
    return (
      <div className="bg-black/30 border border-white/10 rounded-lg px-4 py-4">
        <p className="text-white/50 text-sm">{t('common.loading')}</p>
      </div>
    );
  }

  // GET /publish/{job_id} is validated against YouTubePublishJobStatus, so
  // `progress` is always present and languages_total is the request's
  // target-language count (never 0 -- the backend rejects an empty list).
  const progress: YouTubePublishProgress = job.progress;
  const languagesFailed = progress.languages_failed;
  const failureKey = jobFailureKey(job);
  const pct = Math.round((progress.languages_completed / progress.languages_total) * 100);

  return (
    <div className="space-y-4">
      {/* Header row: state badge + job id */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium ${stateBadgeClass(job.state)}`}
        >
          {job.state}
        </span>
        <span className="text-white/40 text-xs font-mono">{job.job_id}</span>
      </div>

      {/* Progress bar */}
      <div className="space-y-1">
        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-purple-500 rounded-full transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="text-white/50 text-xs">
          {t('youtube.publish.languagesProgress', {
            completed: progress.languages_completed,
            total: progress.languages_total,
          })}
        </p>
      </div>

      {/* Why the job did not succeed (e.g. quota exceeded at the pre-check), from
          error_code -- shown even when there are no per-language failures, so a
          failed job always explains itself instead of leaving a bare "failed"
          badge. Failed languages are listed by code and step only: their error
          strings are backend diagnostics (see the header comment). */}
      {(failureKey || languagesFailed.length > 0) && (
        <div className="bg-red-900/20 border border-red-500/20 rounded-lg px-4 py-3 space-y-1">
          {failureKey && (
            <>
              <p className="text-red-400 text-xs font-medium uppercase tracking-wide">
                {t(
                  job.state === 'partial'
                    ? 'youtube.publish.partialHeading'
                    : 'youtube.publish.failedHeading'
                )}
              </p>
              <p className="text-red-300 text-sm break-words">{t(failureKey)}</p>
            </>
          )}
          {languagesFailed.map((f, i) => (
            <p key={i} className="text-red-300 text-xs">
              <span className="font-medium">{f.language}</span> / {f.step}
            </p>
          ))}
        </div>
      )}

      {/* Current activity */}
      {job.current_language && (
        <p className="text-white/70 text-sm">
          {t('youtube.publish.processingLanguage')}{' '}
          <span className="text-white font-medium">{job.current_language}</span>
          {job.current_step && <span className="text-white/40"> — {job.current_step}</span>}
        </p>
      )}

      {/* Video link */}
      {job.video_id && (
        <a
          href={`https://www.youtube.com/watch?v=${job.video_id}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-purple-400 hover:text-purple-300 text-sm underline underline-offset-2"
        >
          youtube.com/watch?v={job.video_id}
        </a>
      )}

      {/* Cancel button — only while job is running */}
      {!TERMINAL_STATES.has(job.state) && (
        <button
          onClick={() => void handleCancel()}
          disabled={cancelling}
          className="min-h-[44px] px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white/70 hover:text-white disabled:opacity-50 text-sm transition-colors"
        >
          {cancelling ? t('common.processing') : t('youtube.publish.cancelJob')}
        </button>
      )}

      {/* Back button — on any terminal job that is NOT a clean success (failed or
          partial). The view no longer auto-dismisses on these, so the user needs
          an explicit way back to the form once they have read the reason above. */}
      {TERMINAL_STATES.has(job.state) && job.state !== 'completed' && (
        <button
          onClick={() => onDone?.()}
          className="min-h-[44px] px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white/70 hover:text-white text-sm transition-colors"
        >
          {t('common.back')}
        </button>
      )}
    </div>
  );
}
